"use client"

import { type CSSProperties, useEffect, useRef } from "react"
import { usePathname } from "next/navigation"
import cn from "classnames"

import { AnimateText } from "~/components/elements/AnimateText/AnimateText"
import { FadeIn } from "~/components/elements/FadeIn/FadeIn"
import { Asset } from "~/components/primitives/Asset/Asset"
import { Button } from "~/components/primitives/Button/Button"
import buttonStyles from "~/components/primitives/Button/TertiaryButton.module.scss"
import { HtmlRenderer } from "~/components/primitives/HtmlRenderer"
import { Icon } from "~/components/primitives/icon"
import { useModal } from "~/contexts/ModalContext"
import { useMatchMedia } from "~/hooks/useMatchMedia"
import { getModuleConfigProps } from "~/utils/moduleConfig"

import type { CenterCalloutInterface } from "./CenterCallout.interface"
import s from "./CenterCallout.module.scss"

export type CenterCalloutViewProps = Omit<CenterCalloutInterface, "id"> & {
  moduleIndex: number
  className?: string
  style?: CSSProperties
}

export const CenterCalloutView = ({
  moduleIndex,
  useVideoBackground,
  enableOverlay,
  backgroundImage,
  backgroundVideo,
  eyebrow,
  headline,
  body,
  cta,
  config,
  className,
  style,
}: CenterCalloutViewProps) => {
  const configProps = getModuleConfigProps(config)
  const videoRef = useRef<HTMLVideoElement>(null)
  const prefersReducedMotion = useMatchMedia("(prefers-reduced-motion: reduce)")
  const pathname = usePathname()
  const isPeopleRoute = pathname === "/people"
  const { open: openModal } = useModal("careers")
  useEffect(() => {
    if (!videoRef.current) return
    if (prefersReducedMotion) {
      videoRef.current.pause()
    } else {
      videoRef.current.play().catch(() => {})
    }
  }, [prefersReducedMotion])

  const showVideo = useVideoBackground && backgroundVideo?.url
  const plainHeadline = headline?.replace(/<[^>]*>/g, "") || "Callout"

  const ctaStyle = {
    "--cta-text-color": "var(--color-text-inverse)",
  } as CSSProperties

  return (
    <section
      className={cn(s.centerCallout, `stack-module-${moduleIndex}`, className)}
      aria-label={plainHeadline}
      data-module="center-callout"
      {...configProps}
      style={{ ...configProps.style, ...style }}
    >
      {/* Background Media */}
      <div className={s.mediaWrapper} aria-hidden="true">
        {showVideo ? (
          <video
            ref={videoRef}
            className={cn(s.media, s.video)}
            src={backgroundVideo.url}
            poster={backgroundImage?.url || undefined}
            autoPlay
            muted
            loop
            playsInline
            tabIndex={-1}
          />
        ) : (
          backgroundImage && (
            <Asset
              className={s.media}
              asset={backgroundImage}
              imageSizes={{ base: "100vw" }}
            />
          )
        )}
      </div>

      {/* Overlay (tablet/desktop only when enabled) */}
      {enableOverlay && <div className={s.overlay} aria-hidden="true" />}

      {/* Content */}
      <div className={s.contentCard}>
        {eyebrow && (
          <FadeIn className={s.eyebrow}>
            <AnimateText element="p" className={s.eyebrow}>
              {eyebrow}
            </AnimateText>
          </FadeIn>
        )}

        {headline && (
          <FadeIn delay={0.1}>
            <AnimateText element="h2" className={s.headline}>
              <HtmlRenderer inline html={headline} />
            </AnimateText>
          </FadeIn>
        )}

        {body && (
          <HtmlRenderer
            animate
            html={body}
            element="div"
            className={s.body}
            delay={0.2}
          />
        )}

        {/* 
        Below is a temporary hack that forces the CTA to be a button that triggers the Careers contact modal when this module is rendered on the /people page.
        It should eventually be removed and replaced with CMS-level modal trigger functionality.
        */}

        {cta && (isPeopleRoute || cta.url) && (
          <FadeIn element="div" delay={0.3}>
            {isPeopleRoute ? (
              <button
                type="button"
                className={buttonStyles.tertiaryButton}
                onClick={() => openModal()}
                style={ctaStyle}
              >
                <span className={buttonStyles.label}>{cta.label}</span>
                <Icon
                  id="link-arrow"
                  className={buttonStyles.icon}
                  aria-hidden="true"
                />
              </button>
            ) : (
              <Button
                cta={{ ...cta, variant: "tertiary" }}
                theme={configProps["data-typography"] || "light"}
                className={s.cta}
              />
            )}
          </FadeIn>
        )}
      </div>
    </section>
  )
}
